import { fullLabel } from "../components/WhereItIs";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { AuthImg } from "@/components/AuthImg";
import { toast } from "sonner";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { ScanField } from "@/components/ScanField";
import { statusLabel, statusColor } from "@/lib/status";
import { formatShort } from "@/lib/datetime";
import {
  MapPin, ScanLine, CheckCircle2, XCircle, AlertTriangle, Package, Boxes, Truck,
  History, RefreshCw, CheckCheck, Clock, PackageOpen, ArrowRight,
} from "lucide-react";

const RECENT_KEY = "workBin.recent";
const KIND_META = { part: { icon: Package, word: "part" }, container: { icon: Boxes, word: "tote" }, donor: { icon: Truck, word: "truck" } };

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}
function saveRecent(list) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8))); } catch { /* ignore */ }
}

export default function WorkBinPage() {
  const [locations, setLocations] = useState([]);
  const [bin, setBin] = useBackState("bin", null);        // { id, code, label }
  const [contents, setContents] = useState(null);
  const [loadingContents, setLoadingContents] = useState(false);
  const [log, setLog] = useBackState("log", []);
  const [last, setLast] = useState(null);
  const [recent, setRecent] = useState(loadRecent);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {});
  }, []);
  const locById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);

  const pushRecent = useCallback((b) => {
    setRecent((prev) => {
      const next = [{ code: b.code, id: b.id, label: b.label, ts: Date.now() }, ...prev.filter((x) => x.code !== b.code)];
      saveRecent(next);
      return next.slice(0, 8);
    });
  }, []);

  const fetchContents = useCallback((binId) => {
    if (!binId) return;
    setLoadingContents(true);
    api.get(`/locations/${binId}/contents`).then(({ data }) => setContents(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoadingContents(false));
  }, []);

  // Back to this page: the bin he was working is still active, its contents asked for fresh
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (bin?.id) fetchContents(bin.id); }, []);

  // ------- activate a bin (scan / recent chip) -------
  const activateBin = useCallback(async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get("/locations/resolve", { params: { scan: code } });
      const b = { id: data.id, code: data.short_code, label: `${data.short_code} · ${fullLabel(data)}` };
      setBin(b); pushRecent(b); fetchContents(b.id);
      scanAccept(`WORKING ${b.code}`);
      toast.success(`Working ${b.code}`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
    }
  }, [pushRecent, fetchContents, setBin]);

  // ------- scan an item INTO the active bin (auto-detect part vs tote) -------
  const flash = (entry) => { setLast(entry); setLog((l) => [entry, ...l].slice(0, 100)); };
  const okFlash = (e) => {
    const what = e.part?.short_code || e.container?.short_code || e.code || "IN";
    if (e.warn) scanWarn(e.message || what); else scanAccept(what);
    flash({ ok: true, ts: Date.now(), ...e });
  };
  const dupeFlash = (e) => {
    scanWarn(`${e.part?.short_code || e.container?.short_code || "IT"} IS ALREADY IN HERE`);
    flash({ ok: true, already: true, ts: Date.now(), ...e });
  };
  const failFlash = (code, message) => { scanRefuse(message || "THAT DIDN'T GO IN"); flash({ ok: false, code, message, ts: Date.now() }); };

  const putIntoBin = async (code, meta) => {
    if (!code) return;
    if (!bin) { toast.error("Scan a bin to work first."); return; }
    try {
      const { data: r } = await api.get("/scan/resolve", { params: { code } });
      if (r.kind === "part") {
        const { data } = await api.post("/putaway", { part_scan: code, location_id: bin.code,
                                                      provenance: prov("work_bin", meta) });
        if (data.already) return dupeFlash({ code, kind: "part", label: data.part.short_code, sub: "already in this bin — pull tag away", line: `${data.part.short_code} · already in ${bin.code}` });
        if (!data.ok) return failFlash(code, data.message);
        okFlash({ code, kind: "part", label: data.part.short_code, sub: data.part.name, line: `${data.part.short_code} \u2192 ${data.location.short_code}` });
        fetchContents(bin.id);
      } else if (r.kind === "location") {
        const loc = locById[r.id];
        if (loc && loc.type !== "container") return failFlash(code, "That's a fixed location, not a movable tote — scan a part or a tote/pallet.");
        const { data } = await api.post("/containers/move", { container_scan: code, location_scan: bin.code,
                                                              provenance: prov("work_bin", meta) });
        if (!data.ok) return failFlash(code, data.message);
        okFlash({ code, kind: "container", label: data.container.short_code, warn: data.moved_parts === 0,
                  sub: data.moved_parts === 0 ? "0 parts came with it — empty/wrong tote?" : `${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"} came with it`,
                  line: `${data.container.short_code} \u2192 ${data.destination.short_code}` });
        fetchContents(bin.id);
      } else if (r.kind === "donor") {
        return failFlash(code, "That's a truck — place trucks from Move Item or the truck page, not into a bin.");
      } else {
        failFlash(code, "That scan didn't resolve to a part or a tote.");
      }
    } catch (e) {
      failFlash(code, formatApiErrorDetail(e.response?.data?.detail) || "Move failed.");
    }
  };

  const okCount = log.filter((x) => x.ok && !x.warn && !x.already).length;
  const dupCount = log.filter((x) => x.already).length;
  const errCount = log.filter((x) => !x.ok).length;

  const finishBatch = () => {
    scanAccept(`${okCount} IN ${bin?.code || "THE BIN"}`);
    const dup = dupCount > 0 ? ` · ${dupCount} re-read${dupCount === 1 ? "" : "s"} caught` : "";
    toast.success(`Done — ${okCount} item${okCount === 1 ? "" : "s"} into ${bin?.code || "bin"}${dup}.`);
    setLog([]); setLast(null);
  };

  const changeBin = () => { setBin(null); setContents(null); setLast(null); };

  return (
    <div data-testid="work-bin-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Work a Bin</h1>
          <p className="text-sm text-slate-500">Scan a bin to see what's in it, then rapid-fire scan parts or totes into it. Every scan saves on its own — the feed is your proof.</p>
        </div>
        <SoundToggle />
      </div>

      {/* Recent bins strip */}
      {recent.length > 0 && (
        <div data-testid="work-bin-recent" className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Recent bins</span>
          {recent.map((r) => (
            <button key={r.code} data-testid={`work-bin-recent-${r.code}`} onClick={() => activateBin(r.code)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 h-8 text-xs font-mono font-semibold transition-colors ${bin?.code === r.code ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-600 hover:border-[#E0781A] hover:bg-orange-50"}`}>
              <MapPin className="w-3 h-3" />{r.code}
            </button>
          ))}
          <Link to="/moves-history" data-testid="work-bin-view-all" className="text-xs text-[#E0781A] hover:underline inline-flex items-center gap-1 ml-1">
            <History className="w-3.5 h-3.5" />View all
          </Link>
        </div>
      )}

      {!bin ? (
        /* No active bin — scan to start */
        <div data-testid="work-bin-start" className="border border-slate-200 bg-white rounded-xl p-5 max-w-xl">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5 text-[#B01E28]" />Scan a bin or tote to work</Label>
          <ScanField testid="work-bin-scan" submitLabel="Open" placeholder="Scan a bin / tote / pallet, or type its code" onScan={activateBin} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
          <div className="space-y-4">
            {/* Active bin header */}
            <div data-testid="work-bin-active" className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                    <span className="font-display text-xl font-black text-slate-900" data-testid="work-bin-code">{bin.code}</span>
                  </div>
                  <div className="text-sm text-slate-500 font-mono mt-0.5">{bin.label}</div>
                  {contents?.last_activity && <div className="text-[11px] text-slate-400 mt-1">Last activity {formatShort(contents.last_activity)}</div>}
                </div>
                <Button data-testid="work-bin-change" variant="outline" size="sm" className="rounded-lg border-slate-300 bg-white" onClick={changeBin}>Change bin</Button>
              </div>
            </div>

            {/* Scan item into bin */}
            <div className="border border-slate-200 bg-white rounded-xl p-4">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan a part or tote into {bin.code}</Label>
              <ScanField testid="work-bin-item" submitLabel="Put in" continuous autoFocus placeholder="Scan a part or tote…" onScan={putIntoBin} />
            </div>

            {/* Big flash */}
            {last && (() => {
              const tone = !last.ok ? "border-red-500 bg-red-50" : (last.warn || last.already) ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50";
              const Icon = !last.ok ? XCircle : (last.warn || last.already) ? AlertTriangle : CheckCircle2;
              const color = !last.ok ? "text-red-600" : (last.warn || last.already) ? "text-amber-600" : "text-emerald-600";
              return (
                <div data-testid="work-bin-flash" className={`border-2 rounded-xl p-6 text-center ${tone}`}>
                  <Icon className={`w-12 h-12 mx-auto ${color}`} />
                  {last.ok ? (
                    <div className="mt-2">
                      <div className="font-display text-2xl font-black">{last.label}</div>
                      <div className="text-sm text-slate-700">{last.sub}</div>
                      <div className="font-mono text-xs text-slate-600 mt-1">{last.line}</div>
                      {!last.already && !last.warn && <div className="font-mono text-[11px] text-emerald-700 mt-1">✓ saved — pull tag away, scan next</div>}
                    </div>
                  ) : <div className="mt-2 font-mono text-sm text-red-700">{last.message}</div>}
                </div>
              );
            })()}

            {/* Bin contents */}
            <div data-testid="work-bin-contents" className="border border-slate-200 bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-sm uppercase tracking-wide text-slate-700 flex items-center gap-2"><PackageOpen className="w-4 h-4 text-slate-400" />In this bin</h3>
                <button data-testid="work-bin-refresh" onClick={() => fetchContents(bin.id)} className="text-slate-400 hover:text-[#E0781A] transition-colors" title="Refresh contents">
                  <RefreshCw className={`w-4 h-4 ${loadingContents ? "animate-spin" : ""}`} />
                </button>
              </div>
              {!contents ? <div className="text-sm text-slate-400 font-mono py-4 text-center">Loading…</div> : (
                <>
                  <div className="flex gap-3 text-xs text-slate-500 mb-3">
                    <span data-testid="work-bin-part-count"><b className="text-slate-800">{contents.part_count}</b> loose part{contents.part_count === 1 ? "" : "s"}</span>
                    <span>·</span>
                    <span data-testid="work-bin-container-count"><b className="text-slate-800">{contents.container_count}</b> tote{contents.container_count === 1 ? "" : "s"}</span>
                  </div>
                  {contents.containers.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Totes / containers here</div>
                      <div className="flex flex-wrap gap-1.5">
                        {contents.containers.map((c) => (
                          <Link key={c.id} to={`/containers`} data-testid={`work-bin-tote-${c.short_code}`}
                            className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                            <Boxes className="w-3 h-3 text-indigo-500" />{c.short_code}{c.is_lot_crate ? <span className="text-slate-400">· lot</span> : null}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {contents.parts.length === 0 ? (
                    <p className="text-sm text-slate-400">{contents.container_count > 0 ? "No loose parts (parts are inside the totes above)." : "Empty — scan parts or totes in above."}</p>
                  ) : (
                    <div className="space-y-1.5 max-h-[40dvh] overflow-y-auto">
                      {contents.parts.map((p) => (
                        <Link key={p.id} to={`/parts/${p.id}`} data-testid={`work-bin-part-${p.short_code}`}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                          {p.photo ? <AuthImg fileId={p.photo} thumb alt="" className="w-8 h-8 rounded object-cover shrink-0" /> : <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center shrink-0"><Package className="w-4 h-4 text-slate-300" /></div>}
                          <span className="flex-1 min-w-0 text-sm truncate">{p.name || <span className="italic text-slate-400">Unnamed</span>}</span>
                          <Badge className={`rounded-md text-white text-[9px] uppercase font-mono ${statusColor(p.status)}`}>{statusLabel(p.status)}</Badge>
                          <span className="font-mono text-[10px] text-slate-400">{p.short_code}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {log.length > 0 && (
              <Button data-testid="work-bin-finish" onClick={finishBatch} variant="outline" className="w-full rounded-lg border-slate-300 h-11">
                <CheckCheck className="w-4 h-4 mr-1.5" />Done — finish ({okCount} in)
              </Button>
            )}
          </div>

          {/* Puts feed — live proof each scan committed */}
          <div className="border border-slate-200 bg-white rounded-xl overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex justify-between">
              <span>Just put in</span>
              <span><span className="text-emerald-600" data-testid="work-bin-ok-count">{okCount} ok</span> · <span className="text-amber-600" data-testid="work-bin-dup-count">{dupCount} dup</span> · <span className="text-red-600" data-testid="work-bin-err-count">{errCount} err</span></span>
            </div>
            <div className="max-h-[60dvh] overflow-y-auto">
              {log.length === 0 ? <div className="p-6 text-center font-mono text-xs text-slate-400">NO SCANS YET</div>
                : log.map((x, i) => {
                  const Icon = !x.ok ? XCircle : (x.warn || x.already) ? AlertTriangle : CheckCircle2;
                  const M = KIND_META[x.kind];
                  return (
                    <div key={i} className={`flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-xs ${!x.ok ? "bg-red-50" : (x.warn || x.already) ? "bg-amber-50" : ""}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${!x.ok ? "text-red-600" : (x.warn || x.already) ? "text-amber-600" : "text-emerald-600"}`} />
                      {x.ok ? (
                        <span className="font-mono flex items-center gap-1 flex-1 min-w-0">
                          {M?.icon ? <M.icon className="w-3 h-3 text-slate-400 shrink-0" /> : null}<span className="truncate">{x.line}</span>
                        </span>
                      ) : <span className="font-mono text-red-700 truncate flex-1">{x.code}: {x.message}</span>}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
